import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Cloud, Palette, Settings, Sparkles, TrendingUp, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isFreePlanUser } from '@/utils/subscriptionUtils';
import SubscriptionUpgradeModal from '@/components/UI/SubscriptionUpgradeModal';
import screenService from '@/utils/services/ScreenService.js';
import ContentGeneratorService from '@/utils/ContentGeneratorService.js';
import imageProxy from '@/utils/imageProxy.js';

const CreateDynamicContentModal = ({ uid, onClose, onCreated }) => {
  const { currentUserData } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Check if user is on free plan
  if (isFreePlanUser(currentUserData)) {
    return (
      <>
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
            <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">AI Content Generation</h2>
            <p className="text-gray-600 mb-6">Access to AI content generation requires a subscription.</p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Upgrade to Use AI Features
            </button>
          </div>
        </div>
        
        <SubscriptionUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            onClose();
          }}
          feature="ai_features"
        />
      </>
    );
  }

  const [screenName, setScreenName] = useState('');

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [contentService] = useState(new ContentGeneratorService());
  
  // Content type parameters
  const [params, setParams] = useState({
    prompt: '',
    style: 'modern'
  });

  // Load pre-filled data from sessionStorage
  useEffect(() => {
    const prefillScreenName = sessionStorage.getItem('prefillScreenName')
    
    if (prefillScreenName) {
      setScreenName(prefillScreenName)
      sessionStorage.removeItem('prefillScreenName')
    }
  }, [])

  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);



  // Handle parameter changes
  const handleParamChange = (paramName, value) => {
    setParams(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  // Generate preview
  const generatePreview = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🎨 Generating preview with params:', params);
      const result = await contentService.generateContent('custom', params);
      console.log('✅ Preview generated:', result);
      
      // Handle CORS issue using image proxy utility
      const processedUrl = await imageProxy.getImageUrl(result.imageUrl, false);
      setPreviewUrl(processedUrl);
      setGeneratedResult(result);
      console.log('💾 Generated result stored:', result);
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Failed to generate preview. Please check your API configuration.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submit
  const handleCreate = async () => {
    console.log('🔍 handleCreate function called!');
    setError('');
    if (!screenName.trim()) {
      setError('Please provide the screen name');
      return;
    }

    if (!generatedResult) {
      setError('Please generate a preview first');
      return;
    }

    if (!uid) {
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    console.log('🚀 Creating screen with stored result:', generatedResult);
    console.log('🔍 generatedResult.imageUrl:', generatedResult?.imageUrl);
    console.log('🔍 generatedResult keys:', Object.keys(generatedResult || {}));
    setCreating(true);
    try {
      // Use the original image URL from the generated result
      console.log('📥 Fetching image from:', generatedResult.imageUrl);
      const response = await fetch(generatedResult.imageUrl);
      const blob = await response.blob();
      const imageDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      
      // Prepare screen data with dynamic content metadata
      const screenData = {
        name: screenName.trim(),
        contentType: 'custom',
        contentParams: params,
        isDynamic: true,
        lastGenerated: new Date().toISOString()
      };
      
      console.log('💾 Creating screen with data:', screenData);
      // Use ScreenService to create the screen
      console.log('🔄 About to call screenService.createScreen...');
      await screenService.createScreen(screenData, imageDataUrl, uid);
      console.log('✅ screenService.createScreen completed successfully');
      setCreating(false);
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      console.error('Error creating dynamic screen:', err);
      setError('Failed to create screen. ' + (err.message || ''));
      setCreating(false);
    }
  };

  // Handle image download
  const handleDownload = async () => {
    if (!generatedResult?.imageUrl) {
      setError('No image to download. Please generate a preview first.');
      return;
    }

    try {
      console.log('📥 Downloading image from:', generatedResult.imageUrl);
      const response = await fetch(generatedResult.imageUrl);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on prompt and timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const promptWords = params.prompt.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `ai_content_${promptWords}_${timestamp}.png`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Image downloaded successfully:', filename);
    } catch (err) {
      console.error('Error downloading image:', err);
      setError('Failed to download image. Please try again.');
    }
  };

  // Render parameter inputs
  const renderParameters = () => {
    return (
      <div className="space-y-4">
        {/* Prompt Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Describe what you want to display
          </label>
          <textarea
            value={params.prompt || ''}
            onChange={(e) => handleParamChange('prompt', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            placeholder="e.g., 'Welcome message', 'Promotional banner', 'Event announcement', 'Restaurant menu', 'Company information'"
            disabled={loading || creating}
            rows={4}
            style={{ minHeight: '100px', height: '30vh' }}
          />
        </div>

        {/* Style Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Visual Style
          </label>
          <select
            value={params.style || 'modern'}
            onChange={(e) => handleParamChange('style', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            disabled={loading || creating}
          >
            <option value="modern">Modern</option>
            <option value="classic">Classic</option>
            <option value="minimal">Minimal</option>
          </select>
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 overflow-hidden touch-none pointer-events-auto">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-7xl overflow-hidden overflow-y-auto max-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-primary-600" />
            </div>
            <span className="font-bold text-2xl text-gray-900">Create AI Content Screen</span>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-12 py-6 bg-gray-50">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left Column - Configuration */}
            <div className="space-y-4">
              {/* Basic Info */}
              <div>
                <label className="block mb-3 font-medium text-gray-700 text-lg">Screen Name</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-6 py-3 mb-2 text-xl focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="Enter the screen name"
                  value={screenName}
                  onChange={e => setScreenName(e.target.value)}
                  disabled={loading || creating}
                  maxLength={64}
                />
                <p className="text-sm text-gray-500 mb-3">Give your AI-generated content a descriptive name to easily identify it later</p>

              </div>



              {/* Parameters */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Configuration</h3>
                {renderParameters()}
              </div>

              {/* Generate Preview Button */}
              <button
                onClick={generatePreview}
                disabled={loading || creating}
                className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {loading ? 'Generating...' : 'Generate Preview'}
              </button>
            </div>

            {/* Right Column - Preview */}
            <div className="xl:col-span-2 space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-4 min-h-[350px] flex items-center justify-center">
                {loading ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Generating content...</p>
                  </div>
                ) : previewUrl ? (
                  <div className="w-full">
                    {previewUrl.startsWith('data:') ? (
                      <img
                        src={previewUrl}
                        alt="Content Preview"
                        className="max-w-full max-h-[350px] object-contain rounded-lg shadow-lg"
                      />
                    ) : (
                      <div>
                        <img
                          src={previewUrl}
                          alt="Content Preview"
                          className="max-w-full max-h-[350px] object-contain rounded-lg shadow-lg"
                          onError={(e) => {
                            console.warn('Image failed to load due to CORS:', e);
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div className="hidden text-center p-4 bg-gray-100 rounded-lg">
                          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Palette className="w-8 h-8 text-primary-600" />
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Image Generated Successfully</p>
                          <p className="text-xs text-gray-500 mb-2">Preview not available due to CORS restrictions</p>
                          <p className="text-xs text-gray-500">The image will be saved when you create the screen</p>
                          <a 
                            href={previewUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 hover:text-primary-700 underline"
                          >
                            View Image in New Tab
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <Palette className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Configure your content and click "Generate Preview" to see the result</p>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-1">AI-Powered Content</h4>
                    <p className="text-sm text-blue-700">
                      Custom content will be generated from your prompt using advanced AI image generation.
                      The content will be saved as a static image that can be scheduled and displayed on your devices.
                    </p>
                    {generatedResult && (
                      <p className="text-sm text-blue-600 mt-2 font-medium">
                        💡 You can download the generated image or create a screen with it.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="text-red-500 mt-4 text-base">{error}</div>}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-12 py-4 bg-gray-50 border-t border-gray-100">
          <button
            className="px-5 py-3 rounded-lg text-lg text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onClose}
            disabled={loading || creating}
          >
            Close
          </button>
          {generatedResult && (
            <button
              className="px-5 py-3 rounded-lg bg-green-500 text-white text-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-60 flex items-center gap-2"
              onClick={handleDownload}
              disabled={loading || creating}
            >
              <Download className="w-5 h-5" />
              Download Image
            </button>
          )}
          <button
            className="px-5 py-3 rounded-lg bg-primary-500 text-white text-lg font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
            onClick={() => {
              console.log('🔘 Create button clicked!');
              handleCreate();
            }}
            disabled={loading || creating || !generatedResult}
          >
            {creating ? 'Creating...' : 'Create AI Content Screen'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateDynamicContentModal; 